import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { Box, BlueButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import CallIcon from '~Assets/icons/call.svg';
import MailIcon from '~Assets/icons/mail.svg';
import Loader from '~ComponentsImproved/Loader';
import { BookingComment, DiscountBadge, IconText } from '~Scenes/Bookings/sharedStyles';
import { useBookings } from '~Providers/BookingsProvider';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import { formatPhoneNumber } from '~Scenes/Bookings/utils';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1300);
  height: 100%;
  width: 264px;
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BookingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
`;

const BookingTime = styled(Paragraph)`
  margin: 0;
  font-weight: 700;
  font-size: 16px;
`;

const PartySizeText = styled(Paragraph)`
  margin: 0;
  font-weight: 600;
  opacity: 0.8;
`;

interface Props {
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  tableId: string;
  onChequeCreated: (chequeId: string) => void;
}

const BookingDetails: FC<Props> = ({ booking, tableId, onChequeCreated }) => {
  const bookings = useBookings();
  const [loading, setLoading] = useState<boolean>(false);
  const { i18n } = useI18n();
  const tableApi = useTableApi();

  const handleStartBooking = async () => {
    setLoading(true);
    // Update booking status to STARTED
    const chequeId = await bookings.actions.start(booking.id, tableId);

    if (chequeId) {
      onChequeCreated(chequeId);

      tableApi.setBookingMetadata(chequeId, {
        externalId: booking.id,
        comment: booking.comment,
        endsAt: booking.endTime,
        firstName: booking.guestName.split(' ')[0],
        lastName: booking.guestName.split(' ')[1] || '',
        numberOfGuests: booking.partySize,
        mealType: null,
      });
    }

    setLoading(false);
  };

  const startButton = useButton({
    onClick: handleStartBooking,
    disabled: loading,
  });

  return (
    <Wrapper display="flex" flexDirection="column" padding="small" justifyContent="space-between">
      <Box marginTop="small">
        {/* Title: Date and Party Size */}
        <BookingHeader>
          <BookingTitle>
            <BookingTime level={3} color="var(--colorWhite)">
              {booking.startTime} - {booking.endTime}
            </BookingTime>
            {booking.discountPercent && booking.discountPercent > 0 ? (
              <DiscountBadge>{booking.discountPercent}%</DiscountBadge>
            ) : null}
          </BookingTitle>
        </BookingHeader>

        {/* Name and Discount */}
        <Box>
          <Box display="flex" justifyContent="space-between" gap="xs" marginBottom="xs">
            <Paragraph level={3} color="var(--colorBlueGray300)">
              {booking.guestName || 'Unknown Guest'}
            </Paragraph>
            <PartySizeText level={3} color="var(--colorBlueGray300)">
              {booking.partySize} {i18n('Bookings.Guests')}
            </PartySizeText>
          </Box>

          {booking.guestPhone && (
            <IconText>
              <Icon src={CallIcon} color="var(--colorBlueGray400)" height="16px" width="16px" />
              {formatPhoneNumber(booking.guestPhone)}
            </IconText>
          )}

          {booking.guestEmail && !booking.guestEmail.includes('ADMIN_BOOKING') ? (
            <IconText>
              <Icon src={MailIcon} color="var(--colorBlueGray400)" height="16px" width="16px" />
              {booking.guestEmail}
            </IconText>
          ) : null}
        </Box>

        {/* Comment */}
        {booking.comment && <BookingComment>{booking.comment}</BookingComment>}
      </Box>
      <BlueButton {...startButton} fluid size="xl" marginBottom="small">
        {loading ? <Loader color="var(--colorWhite)" size="sm" /> : i18n('Bookings.StartBooking')}
      </BlueButton>
    </Wrapper>
  );
};

export default BookingDetails;
